import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Box, Button, IconButton, Tooltip, useTheme } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import Header from 'shared/components/Header';
import LoadingSpinner from 'shared/components/LoadingSpinner';
import usePersistedColumnVisibility from 'shared/hooks/usePersistedColumnVisibility';
import { tokens } from 'shared/theme';
import usePermissionService from 'shared/api/services/permissionService';
import CreatePermissionDialog from './createPermissionDialog';
import EditPermissionDialog from './editPermissionDialog';
import DisablePermissionDialog from './disablePermissionDialog';
import {
  resolvePermissionServiceCode,
  resolveServiceLabel,
} from 'platform/rbac/Permissions';
import { buildGridToolbarExportProps } from 'shared/utils/tableExportFileName';

const PermissionsPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { columnVisibilityModel, onColumnVisibilityModelChange } =
    usePersistedColumnVisibility('platform.permissions');
  const {
    permissions,
    totalRecords,
    isLoading,
    fetchPermissions,
    createPermission,
    updatePermission,
    deletePermission,
  } = usePermissionService();

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [disableTarget, setDisableTarget] = useState(null);

  const load = useCallback(() => {
    fetchPermissions(paginationModel.page, paginationModel.pageSize);
  }, [fetchPermissions, paginationModel.page, paginationModel.pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  const handleEdit = useCallback((row) => setEditRow(row), []);
  const handleDisableClick = useCallback((row) => setDisableTarget(row), []);

  const rows = useMemo(
    () =>
      permissions.map((p) => ({
        id: p.id,
        serviceCode: p.serviceCode ?? resolvePermissionServiceCode(p.permission),
        serviceLabel: resolveServiceLabel(p.serviceCode ?? resolvePermissionServiceCode(p.permission)),
        permission: p.permission ?? '',
        description: p.description ?? '',
        status: p.status ?? '',
        createdAt: p.createdAt ?? '',
      })),
    [permissions]
  );

  const columns = useMemo(
    () => [
      { field: 'serviceLabel', headerName: 'Service type', flex: 0.8, minWidth: 130 },
      { field: 'permission', headerName: 'Permission key', flex: 1, minWidth: 160 },
      { field: 'description', headerName: 'Description', flex: 1.2, minWidth: 180 },
      { field: 'status', headerName: 'Status', width: 110 },
      { field: 'createdAt', headerName: 'Created', width: 160 },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Box display="flex" alignItems="center" gap={0.25}>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleEdit(row)}
                aria-label="Edit permission"
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={
                'Delete permission permanently'
              }
            >
              <IconButton
                size="small"
                color="error"
                onClick={() => handleDisableClick(row)}
                aria-label="Delete permission"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [handleEdit, handleDisableClick]
  );

  return (
    <Box m="20px">
      <CreatePermissionDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={load}
        createPermission={createPermission}
      />
      <EditPermissionDialog
        open={Boolean(editRow)}
        onClose={() => setEditRow(null)}
        onSuccess={load}
        row={editRow}
        updatePermission={updatePermission}
      />
      <DisablePermissionDialog
        open={Boolean(disableTarget)}
        onClose={() => setDisableTarget(null)}
        onSuccess={load}
        row={disableTarget}
        deletePermission={deletePermission}
      />
      <Header title="Permissions" subtitle="Access control — tenant registry" />
      <Box mt={2} mb={2} display="flex" justifyContent="flex-end" alignItems="center" flexWrap="wrap" gap={1}>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => setCreateOpen(true)}
          sx={{ fontSize: '13px', py: 0.75, px: 1.5 }}
        >
          Add permission
        </Button>
      </Box>
      <Box mt={0} position="relative">
        {isLoading && (
          <Box
            position="absolute"
            zIndex={2}
            left="50%"
            top="40%"
            sx={{ transform: 'translate(-50%, -50%)' }}
          >
            <LoadingSpinner />
          </Box>
        )}
        <Box
          height="72vh"
          sx={{
            opacity: isLoading ? 0.4 : 1,
            pointerEvents: isLoading ? 'none' : 'auto',
            '& .MuiDataGrid-toolbarContainer .MuiButton-text': {
              color: `${colors.grey[100]} !important`,
            },
          }}
        >
          <DataGrid
            disableRowSelectionOnClick
            rows={rows}
            columns={columns}
            rowCount={totalRecords}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={onColumnVisibilityModelChange}
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: buildGridToolbarExportProps({
                title: 'Permissions',
                tableId: 'platform.permissions',
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              }),
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default PermissionsPage;
